import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { IQuestionnaire } from "../../../types/questionnaire";

import { useQuestionnaireData } from "../../../providers/QuestionnaireProvider";

import { QuestionnaireModal } from "../../modals/QuestionnaireModal";

import useGetQuestionnaires from "../../../hooks/questionnaires/useGetQuestionnaires";
import useUpdateQuestionnaire from "../../../hooks/questionnaires/useUpdateQuestionnaire";
import useCreateQuestionnaire from "../../../hooks/questionnaires/useCreateQuestionnaire";
import useDuplicateQuestionnaire from "../../../hooks/questionnaires/useDuplicateQuestionnaire";

const QuestionnaireSelect = () => {
  const navigate = useNavigate();

  const [currentQuestionnaireCode, setCurrentQuestionnaireCode] = useState<
    string | null
  >(null);
  const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
  const [isServiceError, setIsServiceError] = useState<string | null>(null); //eslint-disable-line
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<string>("");
  const [modalTitle, setModalTitle] = useState("");

  const { questionnaireData, setQuestionnaireData } = useQuestionnaireData();

  const location = useLocation();
  const pathName = location.pathname;
  const slug = pathName.split("/")[3];
  const questCode =
    pathName.split("/")[2] !== ":questcode" ? pathName.split("/")[2] : null;

  const {
    data: questionnaires,
    isLoading: isQuestionnaireLoading,
    isError: isQuestionnaireError,
  } = useGetQuestionnaires();

  const { mutate: createQuestionnaire } = useCreateQuestionnaire({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: updateQuestionnaire } = useUpdateQuestionnaire({
    setIsProcessRunning,
    setIsServiceError,
  });

  const { mutate: duplicateQuestionnaire } = useDuplicateQuestionnaire({
    setIsProcessRunning,
    setIsServiceError,
  });

  const blankQuestionnaire: IQuestionnaire = {
    id: 0,
    name: "",
    quest_code: "",
    version: "",
    description: "",
  };

  useEffect(() => {
    if (questionnaires?.length) {
      setCurrentQuestionnaireCode(
        questCode ? questCode : questionnaires[0].quest_code
      );

      if (!questCode) {
        setQuestionnaireData(questionnaires[0]);
      } else {
        const questionnaire = questionnaires.find(
          (questionnaire: IQuestionnaire) =>
            questionnaire.quest_code === questCode
        );
        if (questionnaire) {
          setQuestionnaireData(questionnaire);
        }
      }

      navigate(
        `/questionnaire/${questCode ? questCode : questionnaires[0].quest_code
        }/${slug}`,
        { replace: true }
      );
    }
  }, [questionnaires]); //eslint-disable-line

  if (!questionnaires || isQuestionnaireLoading) return <h6>Loading...</h6>;
  if (isQuestionnaireError) return <h6>Error</h6>;

  const onQuestionnaireChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const questCode = e.target.value;

    if (questCode) {
      setCurrentQuestionnaireCode(questCode);

      const questionnaire = questionnaires.find(
        (questionnaire: IQuestionnaire) =>
          questionnaire.quest_code === questCode
      );
      if (questionnaire) setQuestionnaireData(questionnaire);

      navigate(`/questionnaire/${questCode}/${slug}`);
    }
  };

  const handleQuestionnaireSave = (questionnaire: IQuestionnaire) => {
    switch (action) {
      case "create":
        createQuestionnaire(questionnaire);
        break;
      case "update":
        updateQuestionnaire(questionnaire);
        break;
      case "copy":
        duplicateQuestionnaire({ questionnaire, questCode: questionnaireData?.quest_code ?? "" });
        break;
      default:
        break;
    }
  };

  console.log(questionnaireData);

  return (
    <>
      <QuestionnaireModal
        questionnaireData={
          (action === "create" || action === "copy")
            ? blankQuestionnaire
            : questionnaireData ?? blankQuestionnaire
        }
        title={modalTitle}
        onSave={handleQuestionnaireSave}
        action={action}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      <div className="imm_quest_admin__quest_select_cont">
        <div className="imm_quest_admin__quest_select">
          <select
            value={currentQuestionnaireCode ?? ""}
            onChange={onQuestionnaireChange}
          >
            {questionnaires.map((questionnaire: IQuestionnaire) => (
              <option key={questionnaire.id} value={questionnaire.quest_code}>
                <h4>{questionnaire.name}</h4>
              </option>
            ))}
          </select>
          <h6 className="text_disabled">
            Quest code: {currentQuestionnaireCode} (v{questionnaireData?.version})
          </h6>
        </div>
        <button
          onClick={() => {
            setIsModalOpen(true);
            setModalTitle("Update Questionnaire");
            setAction("update");
          }}
        >
          {isProcessRunning ? (
            <i className="imm_quest_admin__process_loader" />
          ) : (
            "Update"
          )}
        </button>
        <button
          onClick={() => {
            setIsModalOpen(true);
            setModalTitle("Create Questionnaire");
            setAction("create");
          }}
        >
          {isProcessRunning ? (
            <i className="imm_quest_admin__process_loader" />
          ) : (
            "Create New"
          )}
        </button>

        <button onClick={() => {
          setIsModalOpen(true);
          setModalTitle("Copy Questionnaire");
          setAction("copy");
        }}>
          {isProcessRunning ? (
            <i className="imm_quest_admin__process_loader" />
          ) : (
            "Copy"
          )}
        </button>
      </div>
    </>
  );
};

export default QuestionnaireSelect;
